import HeaderAdmin from '../components/HeaderAdmin';
import GraficoPedidosPorHora from '../../../shared/components/GraficoPedidosPorHora';
import SideBarGestaoAdmin from '../components/SideBarGestaoAdmin';
import MobileMenu from '../../../shared/components/MobileMenu';
import { ProdutosContext } from '../../../shared/contexts/ProdutosContext';
import { ItensPedidosContext } from '../../../shared/contexts/ItensPedidosContext';
import PedidosContext from '../../../shared/contexts/PedidosContext';
import { ClientesContext } from '../../../shared/contexts/ClientesContext';

import styles from './GestaoEstabelecimento.module.css';
import { MdOutlineAttachMoney, MdPendingActions, MdPerson, MdAdd, MdStorefront, MdSavings } from "react-icons/md";
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Chart from 'chart.js/auto';

const STATUS_LABELS = ['pendente', 'em andamento', 'pronto', 'entregue'];
const STATUS_CORES = {
    pendente: '#F44336',
    'em andamento': '#FF9800',
    pronto: '#4CAF50',
    entregue: '#2196F3',
};

function GestaoEstabelecimento() {
    const [estabelecimentos, setEstabelecimentos] = useState([]);
    const [estabelecimentoSelecionado, setEstabelecimentoSelecionado] = useState(
        localStorage.getItem('estabelecimento_id') || ''
    );

    const navigate = useNavigate();
    const { produtos } = useContext(ProdutosContext);
    const { pedidos } = useContext(PedidosContext);
    const { itens_do_pedido: itensPedidos } = useContext(ItensPedidosContext);
    const { clientes } = useContext(ClientesContext);
    const API_URL = process.env.REACT_APP_API_URL;

    const fetchEstabelecimentos = async () => {
        try {
            const adminId = localStorage.getItem("admin_id");
            if (!adminId) return;

            const response = await fetch(`${API_URL}/estabelecimentos`, { cache: 'no-store' });

            if (response.ok) {
                const data = await response.json();
                const estabelecimentosDoAdmin = data.filter(est => est.id_admin === Number(adminId));
                setEstabelecimentos(estabelecimentosDoAdmin);
            }
        } catch (error) {
            console.error("Erro ao buscar estabelecimentos:", error);
        }
    };

    useEffect(() => {
        fetchEstabelecimentos();
    }, []);

    const handleEstabelecimentoChange = (e) => {
        const novoId = e.target.value;
        setEstabelecimentoSelecionado(novoId);
        localStorage.setItem('estabelecimento_id', novoId);

        const estabelecimento = estabelecimentos.find(
            est => est.id_estabelecimento === Number(novoId)
        );
        if (estabelecimento) {
            localStorage.setItem('nomeEstabelecimento', estabelecimento.nomeEstabelecimento);
        }
        window.location.reload();
    };

    const estabelecimentoAtual = estabelecimentos.find(
        est => est.id_estabelecimento === Number(estabelecimentoSelecionado)
    );
    const idEstabelecimentoAtual = estabelecimentoSelecionado;

    const pedidosFiltrados = pedidos.filter(p => p.id_estabelecimento === Number(idEstabelecimentoAtual));
    // Todos os pedidos entregues (sem filtro de data) — alimenta o gráfico
    // "Faturamento por dia" e o ticket médio geral logo abaixo, que são
    // métricas de histórico mesmo, não "de hoje".
    const pedidosEntregues = pedidosFiltrados.filter(p => p.status === 'entregue' && !p.is_teste); // pedido de teste (iFood) não conta
    const somaPrecos = pedidosEntregues.reduce((total, pedido) => total + Number(pedido.total || 0), 0);

    // "Faturamento hoje" (card do topo): só pedidos ENTREGUES cujo pedido foi
    // feito hoje — antes somava pedidosEntregues inteiro (todo o histórico),
    // que é o que os outros cálculos acima precisam, mas não bate com o
    // rótulo "hoje" do card.
    const hojeStr = new Date().toDateString();
    const pedidosEntreguesHoje = pedidosEntregues.filter(
        p => p.data_pedido && new Date(p.data_pedido).toDateString() === hojeStr
    );
    const somaPrecosHoje = pedidosEntreguesHoje.reduce((total, pedido) => total + Number(pedido.total || 0), 0);

    // "vs ontem": compara com ontem ATÉ A MESMA HORA — comparar com o dia
    // inteiro de ontem faria hoje sempre parecer pior de manhã.
    const agoraMs = Date.now();
    const ontemMesmaHora = new Date(agoraMs - 24 * 60 * 60 * 1000);
    const inicioOntem = new Date(ontemMesmaHora);
    inicioOntem.setHours(0, 0, 0, 0);
    const somaOntemAteAgora = pedidosEntregues
        .filter(p => {
            if (!p.data_pedido) return false;
            const d = new Date(p.data_pedido);
            return d >= inicioOntem && d <= ontemMesmaHora;
        })
        .reduce((total, pedido) => total + Number(pedido.total || 0), 0);
    const variacaoVsOntem = somaOntemAteAgora > 0
        ? Math.round(((somaPrecosHoje - somaOntemAteAgora) / somaOntemAteAgora) * 100)
        : null;

    const clientesDoEstabelecimento = clientes.filter(cliente =>
        pedidosFiltrados.some(pedido => pedido.id_cliente === cliente.id_cliente)
    );

    const somaHojeFormatada = somaPrecosHoje.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const somaTotalFormatada = somaPrecos.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    // Quanto do WhatsApp o bot resolveu sozinho (ver GET /whatsapp/resumo).
    const [resumoWhatsapp, setResumoWhatsapp] = useState(null);
    useEffect(() => {
        if (!idEstabelecimentoAtual) return;
        let vivo = true;
        fetch(`${API_URL}/whatsapp/resumo?id_estabelecimento=${idEstabelecimentoAtual}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
            .then(res => (res.ok ? res.json() : null))
            .then(dados => { if (vivo) setResumoWhatsapp(dados); })
            .catch(() => {});
        return () => { vivo = false; };
    }, [idEstabelecimentoAtual, API_URL]);

    const canvasRef = useRef(null);
    const chartRef = useRef(null);
    const pedidosPorDiaRef = useRef(null);
    const pedidosPorDiaInstance = useRef(null);

    const totalPorData = pedidosEntregues.reduce((acc, pedido) => {
        const data = new Date(pedido.data_pedido).toLocaleDateString('pt-BR');
        acc[data] = (acc[data] || 0) + Number(pedido.total || 0);
        return acc;
    }, {});

    const labels = Object.keys(totalPorData);
    const dataValores = Object.values(totalPorData);

    const ticketMedioPorData = pedidosEntregues.reduce((acc, pedido) => {
        const data = new Date(pedido.data_pedido).toLocaleDateString('pt-BR');
        if (!acc[data]) acc[data] = { total: 0, quantidade: 0 };
        acc[data].total += Number(pedido.total || 0);
        acc[data].quantidade += 1;
        return acc;
    }, {});

    const valoresTicketMedio = Object.values(ticketMedioPorData).map(item => item.total / item.quantidade);
    const ticketMedioGeral = pedidosEntregues.length ? somaPrecos / pedidosEntregues.length : 0;
    const ticketMedioFormatado = ticketMedioGeral.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const maiorTicketMedio = Math.max(1, ...valoresTicketMedio);

    const pedidosPorData = pedidosFiltrados.reduce((acc, pedido) => {
        const data = new Date(pedido.data_pedido).toLocaleDateString('pt-BR');
        acc[data] = (acc[data] || 0) + 1;
        return acc;
    }, {});

    const labelsPedidosPorDia = Object.keys(pedidosPorData);
    const valoresPedidosPorDia = Object.values(pedidosPorData);

    const statusCounts = pedidosFiltrados.reduce((acc, pedido) => {
        const status = pedido.status || 'pendente';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});
    const totalStatus = STATUS_LABELS.reduce((total, status) => total + (statusCounts[status] || 0), 0) || 1;

    const contagemItens = {};
    itensPedidos.forEach(item => { if (!item.id_produto) return; /* item iFood sem vínculo */ contagemItens[item.id_produto] = (contagemItens[item.id_produto] || 0) + 1; });
    const itensOrdenados = Object.entries(contagemItens)
        .map(([id, quantidade]) => {
            const produto = produtos.find(p => p.id_item === Number(id));
            return { nome: produto ? produto.nome_item : `Produto ${id}`, quantidade };
        })
        .sort((a, b) => b.quantidade - a.quantidade)
        .slice(0, 5);

    useEffect(() => {
        if (chartRef.current) chartRef.current.destroy();
        chartRef.current = new Chart(canvasRef.current, {
            type: 'bar',
            data: { labels, datasets: [{ label: 'Total em R$ por dia', data: dataValores, backgroundColor: '#d33333', barThickness: 40 }] },
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `R$ ${c.parsed.y.toLocaleString('pt-BR')}` } } },
                scales: {
                    y: { beginAtZero: true, ticks: { color: '#5b5f66', callback: (v) => `R$ ${v.toLocaleString('pt-BR')}`, font: { size: window.innerWidth < 768 ? 10 : 12 } }, grid: { color: 'rgba(20,22,26,0.08)' } },
                    x: { ticks: { color: '#5b5f66', font: { size: window.innerWidth < 768 ? 10 : 12 } }, grid: { display: false } },
                },
            },
        });
    }, [pedidos]);

    useEffect(() => {
        if (pedidosPorDiaInstance.current) pedidosPorDiaInstance.current.destroy();
        pedidosPorDiaInstance.current = new Chart(pedidosPorDiaRef.current, {
            type: 'bar',
            data: { labels: labelsPedidosPorDia, datasets: [{ label: 'Pedidos por dia', data: valoresPedidosPorDia, backgroundColor: '#2196F3', barThickness: 40 }] },
            options: {
                responsive: true, maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.parsed.y} pedidos` } } },
                scales: {
                    y: { beginAtZero: true, ticks: { color: '#5b5f66', stepSize: 1, font: { size: window.innerWidth < 768 ? 10 : 12 } }, grid: { color: 'rgba(20,22,26,0.08)' } },
                    x: { ticks: { color: '#5b5f66', font: { size: window.innerWidth < 768 ? 10 : 12 } }, grid: { display: false } },
                },
            },
        });
    }, [pedidos]);

    return (
        <div className={styles.secaoPrincipal}>
            <MobileMenu />
            <SideBarGestaoAdmin className={styles.sidebar} />
            <div className={styles.containerDashboard}>
                <HeaderAdmin />

                <div className={styles.contentWrapper}>

                    {/* Trilho: seletor + criar estabelecimento */}
                    <div className={styles.seletorContainer}>
                        <div className={styles.seletorRow}>
                            <div className={styles.seletorWrapper}>
                                <span className={styles.dotStatus}></span>
                                <select
                                    id="estabelecimento"
                                    value={estabelecimentoSelecionado}
                                    onChange={handleEstabelecimentoChange}
                                    className={styles.seletorEstabelecimento}
                                >
                                    <option value="">Selecione um estabelecimento</option>
                                    {estabelecimentos.map((est) => (
                                        <option key={est.id_estabelecimento} value={est.id_estabelecimento}>
                                            {est.nomeEstabelecimento}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button
                                className={styles.btnCriarEstabelecimento}
                                onClick={() => navigate('/cadastroestabelecimento')}
                            >
                                <MdAdd className={styles.iconePlus} />
                                Criar loja
                            </button>
                        </div>
                    </div>

                    {/* Comandas de resumo */}
                    <div className={styles.contentDashboard}>
                        <section className={styles.resumoDashboard}>
                            <ul className={styles.listaBlocos}>
                                <li className={styles.blocoResumo}>
                                    <div className={styles.stubHead}>
                                        <div className={styles.textoBloco}><h3>Faturamento hoje</h3></div>
                                        <MdOutlineAttachMoney className={styles.iconeCard} />
                                    </div>
                                    <div className={styles.textoBloco}><p>{somaHojeFormatada}</p></div>
                                    <span className={`${styles.variacao} ${variacaoVsOntem === null ? '' : variacaoVsOntem >= 0 ? styles.variacaoSobe : styles.variacaoDesce}`}>
                                        {variacaoVsOntem === null
                                            ? 'Sem vendas ontem até esta hora'
                                            : `${variacaoVsOntem >= 0 ? '▲' : '▼'} ${Math.abs(variacaoVsOntem)}% vs ontem até agora`}
                                    </span>
                                </li>
                                <li className={`${styles.blocoResumo} ${styles.blocoTotal}`}>
                                    <div className={styles.stubHead}>
                                        <div className={styles.textoBloco}><h3>Faturamento total</h3></div>
                                        <MdSavings className={styles.iconeCard} />
                                    </div>
                                    <div className={styles.textoBloco}><p>{somaTotalFormatada}</p></div>
                                    <span className={styles.variacao}>{pedidosEntregues.length} pedido{pedidosEntregues.length === 1 ? '' : 's'} entregue{pedidosEntregues.length === 1 ? '' : 's'} desde o início</span>
                                </li>
                                <li className={styles.blocoResumo}>
                                    <div className={styles.stubHead}>
                                        <div className={styles.textoBloco}><h3>Pendentes</h3></div>
                                        <MdPendingActions className={styles.iconeCard} />
                                    </div>
                                    <div className={styles.textoBloco}><p>{pedidosFiltrados.filter(p => p.status === "pendente").length || 0}</p></div>
                                </li>
                                <li className={styles.blocoResumo}>
                                    <div className={styles.stubHead}>
                                        <div className={styles.textoBloco}><h3>Clientes</h3></div>
                                        <MdPerson className={styles.iconeCard} />
                                    </div>
                                    <div className={styles.textoBloco}><p>{clientesDoEstabelecimento.length}</p></div>
                                </li>
                            </ul>
                        </section>
                    </div>

                    {/* Fichas de relatório */}
                    <section className={styles.relatoriosGrid}>
                        <div className={styles.relatorio}>
                            <h3 className={styles.tituloTicket}><span className={styles.diamante}></span>Faturamento por dia</h3>
                            <div className={styles.chartPlot}>
                                <canvas ref={canvasRef}></canvas>
                            </div>
                        </div>

                        <div className={styles.relatorio}>
                            <h3 className={styles.tituloTicket}><span className={styles.diamante}></span>Status dos pedidos</h3>
                            <div className={styles.statusTrack}>
                                {STATUS_LABELS.map(status => {
                                    const pct = ((statusCounts[status] || 0) / totalStatus) * 100;
                                    return pct > 0 ? (
                                        <span key={status} className={styles.statusSegmento} style={{ width: `${pct}%`, backgroundColor: STATUS_CORES[status] }}></span>
                                    ) : null;
                                })}
                            </div>
                            <div className={styles.statusMiniCards}>
                                {STATUS_LABELS.map(status => (
                                    <div key={status} className={styles.statusMiniCard}>
                                        <div className={styles.qtd} style={{ color: STATUS_CORES[status] }}>{statusCounts[status] || 0}</div>
                                        <div className={styles.lbl}>{status}</div>
                                    </div>
                                ))}
                            </div>
                            <div className={styles.statusLegenda}>
                                {STATUS_LABELS.map(status => (
                                    <span key={status} className={styles.legendaItem}>
                                        <span className={styles.legendaDot} style={{ backgroundColor: STATUS_CORES[status] }}></span>
                                        {status}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className={styles.relatorio}>
                            <h3 className={styles.tituloTicket}><span className={styles.diamante}></span>Top 5 itens mais pedidos</h3>
                            <div className={styles.rankingLista}>
                                {itensOrdenados.length ? itensOrdenados.map((item, i) => (
                                    <div key={item.nome} className={styles.rankingItem}>
                                        <span className={styles.rankingIndice}>{String(i + 1).padStart(2, '0')}</span>
                                        <span className={styles.rankingNome}>{item.nome}</span>
                                        <span className={styles.rankingQtd}>{item.quantidade} pedido{item.quantidade === 1 ? '' : 's'}</span>
                                    </div>
                                )) : (
                                    <p className={styles.rankingVazio}>Sem pedidos registrados ainda.</p>
                                )}
                            </div>
                        </div>

                        <div className={styles.relatorio}>
                            <h3 className={styles.tituloTicket}><span className={styles.diamante}></span>WhatsApp: bot x atendente</h3>
                            {resumoWhatsapp && resumoWhatsapp.total.pedidos > 0 ? (() => {
                                const r = resumoWhatsapp.ultimos_30_dias.pedidos > 0 ? resumoWhatsapp.ultimos_30_dias : resumoWhatsapp.total;
                                const periodo = resumoWhatsapp.ultimos_30_dias.pedidos > 0 ? 'últimos 30 dias' : 'desde o início';
                                return (
                                    <>
                                        <div className={styles.ticketMedioValor}>
                                            <span className={styles.dotBullet}></span>
                                            <span className={styles.ticketMedioNumero}>{r.percentual_bot}%</span>
                                        </div>
                                        <p className={styles.whatsappLegenda}>dos pedidos do WhatsApp foram resolvidos só pelo bot ({periodo})</p>
                                        <div className={styles.statusTrack}>
                                            {r.resolvidos_pelo_bot > 0 && <span className={styles.statusSegmento} style={{ width: `${(r.resolvidos_pelo_bot / r.pedidos) * 100}%`, backgroundColor: '#4CAF50' }}></span>}
                                            {r.com_atendente > 0 && <span className={styles.statusSegmento} style={{ width: `${(r.com_atendente / r.pedidos) * 100}%`, backgroundColor: '#2196F3' }}></span>}
                                        </div>
                                        <div className={`${styles.statusMiniCards} ${styles.miniCardsDois}`}>
                                            <div className={styles.statusMiniCard}><div className={styles.qtd} style={{ color: '#4CAF50' }}>{r.resolvidos_pelo_bot}</div><div className={styles.lbl}>só o bot</div></div>
                                            <div className={styles.statusMiniCard}><div className={styles.qtd} style={{ color: '#2196F3' }}>{r.com_atendente}</div><div className={styles.lbl}>com atendente</div></div>
                                            <div className={styles.statusMiniCard}><div className={styles.qtd}>{r.pedidos}</div><div className={styles.lbl}>pedidos</div></div>
                                            <div className={styles.statusMiniCard}><div className={styles.qtd}>{r.faturamento.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div><div className={styles.lbl}>faturado</div></div>
                                        </div>
                                    </>
                                );
                            })() : (
                                <p className={styles.rankingVazio}>Sem pedidos do WhatsApp ainda.</p>
                            )}
                        </div>

                        <div className={styles.relatorio}>
                            <h3 className={styles.tituloTicket}><span className={styles.diamante}></span>Ticket médio diário</h3>
                            <div className={styles.ticketMedioValor}>
                                <span className={styles.dotBullet}></span>
                                <span className={styles.ticketMedioNumero}>{ticketMedioFormatado}</span>
                            </div>
                            <div className={styles.sparkRow}>
                                {valoresTicketMedio.length ? valoresTicketMedio.slice(-7).map((valor, i, arr) => (
                                    <div
                                        key={i}
                                        className={`${styles.sparkBar} ${i === arr.length - 1 ? styles.sparkAtivo : ''}`}
                                        style={{ height: `${Math.max(6, (valor / maiorTicketMedio) * 100)}%` }}
                                    ></div>
                                )) : <div className={styles.sparkBar} style={{ height: '6%' }}></div>}
                            </div>
                        </div>

                        <div className={styles.relatorio}>
                            <h3 className={styles.tituloTicket}><span className={styles.diamante}></span>Número de pedidos por dia</h3>
                            <div className={styles.chartPlot}>
                                <canvas ref={pedidosPorDiaRef}></canvas>
                            </div>
                        </div>

                        <div className={styles.graficoHoraWrapper}>
                            <GraficoPedidosPorHora pedidos={pedidosFiltrados} titulo="Vendas por Hora" />
                        </div>
                    </section>

                </div>
            </div>
        </div>
    );
}

export default GestaoEstabelecimento;
